import React from 'react'
import useThemeClass from 'utils/hooks/useThemeClass'

// The fallback is its own function component so it can read the theme colour
// through the hook — an error boundary has to be a class (only classes get
// getDerivedStateFromError / componentDidCatch), and a class cannot call hooks.
const ErrorFallback = ({ error, onReload }) => {
	const { bgThemeSoft, textTheme } = useThemeClass();
	const isDev = process.env.NODE_ENV !== 'production'

	return (
		<div className="flex min-h-screen w-full flex-col items-center justify-center gap-4 bg-gray-50 px-6 text-center dark:bg-gray-900">
			<div className={`flex h-16 w-16 items-center justify-center rounded-full ${bgThemeSoft} dark:bg-gray-800`}>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					fill="none"
					viewBox="0 0 24 24"
					strokeWidth={1.8}
					stroke="currentColor"
					className={`h-8 w-8 ${textTheme}`}
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
					/>
				</svg>
			</div>

			<h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">
				Something went wrong
			</h2>
			<p className="max-w-sm text-sm text-gray-500 dark:text-gray-400">
				An unexpected error occurred while loading this page. Please reload to
				try again — if it keeps happening, make sure the server is running.
			</p>

			<button
				type="button"
				onClick={onReload}
				className="cg-btn mt-2 inline-flex items-center gap-2"
			>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					fill="none"
					viewBox="0 0 24 24"
					strokeWidth={2}
					stroke="currentColor"
					className="h-4 w-4"
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						d="M16.023 9.348h4.992V4.356M3.86 14.652H8.85v4.992M4.031 9.348a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.992v4.992m-.001 0h-4.992m-.386 9.356a8.25 8.25 0 01-13.803 3.7l-3.181-3.182m0 4.992v-4.992m.001 0h4.992"
					/>
				</svg>
				Reload
			</button>

			{isDev && error ? (
				<pre className="mt-4 max-w-xl overflow-auto rounded-md bg-gray-100 p-3 text-left text-xs text-red-600 dark:bg-gray-800 dark:text-red-400">
					{String(error?.message || error)}
				</pre>
			) : null}
		</div>
	)
}

class ErrorBoundary extends React.Component {
	constructor(props) {
		super(props)
		this.state = { hasError: false, error: null }
	}

	static getDerivedStateFromError(error) {
		return { hasError: true, error }
	}

	componentDidCatch(error, info) {
		// eslint-disable-next-line no-console
		console.error('Unhandled UI error:', error, info)
	}

	handleReload = () => {
		this.setState({ hasError: false, error: null })
		window.location.reload()
	}

	render() {
		if (this.state.hasError) {
			return <ErrorFallback error={this.state.error} onReload={this.handleReload} />
		}
		return this.props.children
	}
}

export default ErrorBoundary
